/**
 * Cuerpo de la ficha mientras llega el detalle de la tarea: la misma estructura que `VistaTarea`
 * (cabecera, propiedades, textos y adjuntos) con esqueletos donde irán los datos.
 *
 * Lo conocido se muestra al instante: si la tarea venía en la lista del tablero (`tareaDeLasListas`),
 * su título es real; abierta por enlace directo (`?tarea=12`) también el título es esqueleto. Las
 * etiquetas de las propiedades no dependen del servidor y se ven siempre.
 *
 * No se usa `placeholderData` de TanStack Query con la tarea de la lista: el detalle (`TareaDetalle`)
 * trae más campos (descripción, eventos…) y rellenarlos a medias haría que la ficha mostrara datos
 * vacíos como si fueran reales. Se lee la caché solo para la barra y el título.
 */
import type { QueryClient } from '@tanstack/react-query';
import type { ListaTareas, Tarea } from '@cdtec-backlog/contrato';
import { Esqueleto, TextoCargando, type AnchoEsqueleto } from '../../componentes/Esqueleto';
import { MiniaturasCargando } from '../adjuntos/ListaAdjuntos';
import './FichaCargando.css';

/** La tarea tal como vino en alguna lista ya cargada (tablero, lista, aviso de cerradas), si está. */
export function tareaDeLasListas(cliente: QueryClient, id: number): Tarea | undefined {
  for (const [, lista] of cliente.getQueriesData<ListaTareas>({ queryKey: ['tareas'] })) {
    const tarea = lista?.items.find((item) => item.id === id);
    if (tarea) return tarea;
  }
  return undefined;
}

const PROPIEDADES: [string, AnchoEsqueleto][] = [
  ['Prioridad', 50],
  ['Tipo', 60],
  ['Responsable', 80],
  ['Proyecto', 60],
  ['Inicio', 50],
  ['Actualizada', 60],
];

export function FichaCargando({ titulo, mostrar }: { titulo?: string; mostrar: boolean }) {
  return (
    <>
      <TextoCargando>Cargando la tarea…</TextoCargando>
      <div className={`vista ficha-carga${mostrar ? '' : ' carga--espera'}`} aria-busy="true">
        <div className="vista__cab">
          {titulo ? (
            <h2 className="vista__titulo">{titulo}</h2>
          ) : (
            <div className="ficha-carga__titulo" aria-hidden="true">
              <Esqueleto forma="titulo" ancho={90} />
              <Esqueleto forma="titulo" ancho={60} />
            </div>
          )}
          <div className="vista__chips" aria-hidden="true">
            <Esqueleto forma="chip" />
            <Esqueleto forma="chip" />
          </div>
        </div>

        <dl className="vista__props">
          {PROPIEDADES.map(([nombre, ancho]) => (
            <div key={nombre} className="vista__prop">
              <dt>{nombre}</dt>
              <dd className="ficha-carga__valor" aria-hidden="true">
                <Esqueleto ancho={ancho} />
              </dd>
            </div>
          ))}
        </dl>

        <div className="vista__textos" aria-hidden="true">
          <div>
            <h3 className="cedit__etiqueta">Qué se necesita y para qué</h3>
            <div className="ficha-carga__parrafo">
              <Esqueleto ancho={100} />
              <Esqueleto ancho={90} />
              <Esqueleto ancho={60} />
            </div>
          </div>
          <div>
            <h3 className="seccion-titulo">Adjuntos</h3>
            <MiniaturasCargando />
          </div>
        </div>
      </div>
    </>
  );
}
